const PC_STATE_KEY = "peptide-command-v1";
const PC_META_KEY = "peptide-command-inventory-metadata-v2";
const PC_DEMO_VIAL_KEY = "peptide-command-demo-vial-v3";
const PC_ALERT_LOG_KEY = "peptide-command-alert-log-v3";

const pc$ = (q, r = document) => r.querySelector(q);
const pc$$ = (q, r = document) => [...r.querySelectorAll(q)];
const pcRead = (key, fallback = {}) => { try { return JSON.parse(localStorage.getItem(key) || JSON.stringify(fallback)); } catch { return fallback; } };
const pcWrite = (key, value) => localStorage.setItem(key, JSON.stringify(value));
const pcState = () => pcRead(PC_STATE_KEY, {});
const pcMeta = () => pcRead(PC_META_KEY, {});
const pcSaveMeta = value => pcWrite(PC_META_KEY, value);
const pcEsc = value => String(value ?? "").replace(/[&<>'"]/g, c => ({"&":"&amp;","<":"&lt;",">":"&gt;","'":"&#39;",'"':"&quot;"}[c]));
const pcDateKey = date => `${date.getFullYear()}-${String(date.getMonth()+1).padStart(2,"0")}-${String(date.getDate()).padStart(2,"0")}`;
const pcToday = () => new Date(new Date().getFullYear(), new Date().getMonth(), new Date().getDate());
const pcLocalDate = value => value ? new Date(`${value}T00:00:00`) : null;
const pcShortDate = value => { const d = typeof value === "string" ? pcLocalDate(value) : value; return d && !Number.isNaN(d.valueOf()) ? d.toLocaleDateString("en-US", {month:"short", day:"numeric", year:"numeric"}) : "Not set"; };
const pcNum = (value, decimals = 3) => new Intl.NumberFormat("en-US", {maximumFractionDigits:decimals}).format(Number(value) || 0);
const pcMg = (value, unit) => unit === "mcg" ? Number(value) / 1000 : Number(value);
const pcFormatTime = value => { if (!value) return "Any time"; const [h,m] = value.split(":").map(Number); return new Date(2000,0,1,h,m).toLocaleTimeString("en-US",{hour:"numeric",minute:"2-digit"}); };

function pcSupply(item) {
  const vials = Math.floor(Number(item.vialsOnHand) || 0);
  const perVial = Math.floor(Number(item.dosesPerVial) || 0);
  const used = Math.max(0, Math.floor(Number(item.dosesUsed) || 0));
  if (!vials || !perVial) return null;
  const total = vials * perVial;
  const remaining = Math.max(0, total - used);
  const inVialUsed = used % perVial;
  const current = remaining ? Math.min(remaining, inVialUsed ? perVial - inVialUsed : perVial) : 0;
  const finished = Math.min(vials, Math.floor(Math.min(used, total) / perVial));
  const vialNumber = remaining ? Math.min(vials, finished + 1) : vials;
  return {vials, perVial, used, total, remaining, current, finished, vialNumber};
}

function pcOccurs(schedule, date) {
  const start = pcLocalDate(schedule.start);
  if (!start || start > date) return false;
  const key = pcDateKey(date);
  if (schedule.pauseDate && key >= schedule.pauseDate && (!schedule.restartDate || key < schedule.restartDate)) return false;
  if (schedule.frequency === "daily") return true;
  if (schedule.frequency === "weekly") return date.getDay() === Number(schedule.days?.[0] ?? start.getDay());
  if (schedule.frequency === "custom") return (schedule.days || []).map(Number).includes(date.getDay());
  if (schedule.frequency === "as-needed") return key === schedule.start;
  return false;
}

function pcLogId(schedule, date) { return `${pcDateKey(date)}:${schedule.id}`; }

function pcNextOccurrence(schedule, st, maxDays = 365) {
  const today = pcToday();
  for (let offset = 0; offset <= maxDays; offset++) {
    const d = new Date(today); d.setDate(today.getDate() + offset);
    if (pcOccurs(schedule, d) && !st.logs?.[pcLogId(schedule, d)]) return d;
  }
  return null;
}

function pcProjection(item, st) {
  const detail = pcSupply(item);
  const schedule = (st.schedules || []).find(s => s.id === item.scheduleId);
  if (!detail || !schedule || schedule.frequency === "as-needed" || detail.remaining <= 0) {
    return {detail, schedule, next: schedule ? pcNextOccurrence(schedule, st) : null, currentEnd:null, supplyEnd:null, reorder:null};
  }
  let currentLeft = detail.current;
  let totalLeft = detail.remaining;
  let currentEnd = null;
  let supplyEnd = null;
  const today = pcToday();
  for (let guard = 0; guard < 3650 && totalLeft > 0; guard++) {
    const d = new Date(today); d.setDate(today.getDate() + guard);
    if (!pcOccurs(schedule, d)) continue;
    if (guard === 0 && st.logs?.[pcLogId(schedule, d)]) continue;
    totalLeft--;
    if (currentLeft > 0) currentLeft--;
    if (!currentEnd && currentLeft === 0) currentEnd = new Date(d);
    if (totalLeft === 0) supplyEnd = new Date(d);
  }
  let reorder = null;
  if (supplyEnd) {
    reorder = new Date(supplyEnd);
    reorder.setDate(reorder.getDate() - Math.max(0, Number(item.reorderLeadDays ?? 7)));
  }
  return {detail, schedule, next: pcNextOccurrence(schedule, st), currentEnd, supplyEnd, reorder};
}

function pcVialArt(item, detail, meta, compact = false) {
  const pct = detail?.perVial ? Math.max(4, Math.min(100, (detail.current / detail.perVial) * 100)) : 8;
  const amount = meta?.vialAmount ? `${pcNum(meta.vialAmount)} ${meta.vialUnit || "mg"}` : "Vial";
  return `<div class="pc3-vial-art ${compact ? "is-compact" : ""}" style="--pc-fill:${pct}%" aria-hidden="true">
    <div class="pc3-vial-cap"></div><div class="pc3-vial-neck"></div>
    <div class="pc3-vial-glass"><div class="pc3-vial-liquid"></div><div class="pc3-vial-shine"></div>
      <div class="pc3-vial-label"><small>PEPTIDE COMMAND</small><b>${pcEsc((item?.name || "PEPTIDE").slice(0,20))}</b><span>${pcEsc(amount)}</span></div>
    </div>
  </div>`;
}

function pcActiveSupplies(st) {
  const meta = pcMeta();
  return (st.inventory || []).map(item => ({item, meta:meta[item.id] || {}, projection:pcProjection(item, st)})).filter(x => x.projection.detail?.remaining > 0);
}

let pcPreviousVialCounts = new Map();

function pcRenderRoutine() {
  if (!pc$("#peptide-command-app")) return;
  const dashboard = pc$("#inventory-view");
  const scheduleCard = pc$("#inventory-view .table-card");
  if (!dashboard || !scheduleCard) return;
  let section = pc$("#pc3-routine-command");
  if (!section) {
    section = document.createElement("section");
    section.id = "pc3-routine-command";
    section.className = "card span-2 pc3-routine-command";
    scheduleCard.before(section);
  }
  const st = pcState();
  const supplies = pcActiveSupplies(st);
  const nextAll = (st.schedules || []).map(s => ({s, d:pcNextOccurrence(s, st)})).filter(x => x.d).sort((a,b)=>a.d-b.d)[0];
  const totalRemaining = supplies.reduce((sum, x) => sum + x.projection.detail.remaining, 0);

  if (!supplies.length) {
    section.innerHTML = `<div class="pc3-command-head"><div><p class="eyebrow">MY ROUTINE</p><h3>Everything important, one glance.</h3><p>Add a peptide supply to unlock vial intelligence, supply countdowns, and reconstitution tracking.</p></div><button class="primary-btn" data-pc-open-inventory>＋ Add a vial</button></div>
      <div class="pc3-routine-empty"><span>◇</span><div><strong>No active vial yet</strong><small>My Peptides will calculate doses per vial from the verified amounts you enter.</small></div></div>`;
    return;
  }

  const cards = supplies.map(({item, meta, projection}) => {
    const d = projection.detail;
    const old = pcPreviousVialCounts.get(item.id);
    const changed = Number.isFinite(old) && old !== d.current;
    pcPreviousVialCounts.set(item.id, d.current);
    const concentration = meta.vialAmount && meta.diluentMl ? pcMg(meta.vialAmount, meta.vialUnit) / Number(meta.diluentMl) : null;
    const sub = [meta.diluentMl > 0 ? `${pcNum(meta.diluentMl)} mL BAC water / diluent` : "", concentration ? `${pcNum(concentration)} mg/mL` : ""].filter(Boolean).join(" · ");
    return `<article class="pc3-vial-intel ${changed ? "pc3-count-changed" : ""}" data-pc-open-inventory tabindex="0" role="button" aria-label="Open ${pcEsc(item.name)} inventory">
      ${pcVialArt(item,d,meta)}
      <div class="pc3-vial-intel-copy">
        <div class="pc3-vial-kicker"><span class="pc3-live-dot"></span> VIAL ${d.vialNumber} OF ${d.vials} · IN USE</div>
        <h4>${pcEsc(item.name)}</h4>
        <p>${pcEsc(sub || `${d.perVial} calculated whole doses per vial`)}</p>
        <div class="pc3-vial-metrics">
          <div><strong>${d.current}</strong><span>doses left<br>in current vial</span></div>
          <div><strong>${d.remaining}</strong><span>total doses<br>remaining</span></div>
          <div><strong>${projection.next ? pcShortDate(projection.next) : "—"}</strong><span>next planned<br>entry</span></div>
          <div><strong>${projection.currentEnd ? pcShortDate(projection.currentEnd) : "—"}</strong><span>estimated vial<br>finish</span></div>
        </div>
        <div class="pc3-vial-foot"><span>${meta.reconstitutedDate ? `Reconstituted ${pcShortDate(meta.reconstitutedDate)}` : "Add reconstitution date"}</span><span>${projection.reorder ? `Reorder by ${pcShortDate(projection.reorder)}` : projection.supplyEnd ? `Supply through ${pcShortDate(projection.supplyEnd)}` : "Supply timing pending"}</span></div>
      </div>
    </article>`;
  }).join("");

  section.innerHTML = `<div class="pc3-command-head"><div><p class="eyebrow">MY ROUTINE · VIAL INTELLIGENCE</p><h3>What you used. What is left. What comes next.</h3><p>Your home command center updates automatically as completed entries use doses from linked supplies.</p></div><button class="secondary-btn" data-pc-open-inventory>Manage My Peptides →</button></div>
    <div class="pc3-command-strip">
      <div><span>Next planned</span><strong>${nextAll ? pcEsc(nextAll.s.name) : "Nothing scheduled"}</strong><small>${nextAll ? `${pcShortDate(nextAll.d)} · ${pcFormatTime(nextAll.s.time)}` : "Add a clinician-provided schedule"}</small></div>
      <div><span>Active vials</span><strong>${supplies.length}</strong><small>${supplies.length === 1 ? "1 peptide supply in use" : `${supplies.length} peptide supplies in use`}</small></div>
      <div><span>Total supply</span><strong>${totalRemaining} doses</strong><small>Across active tracked inventory</small></div>
    </div>
    <div class="pc3-vial-carousel" aria-label="Active vial intelligence">${cards}</div>`;
}
